import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Star, BadgeCheck, MapPin, Briefcase, ShoppingBag, GraduationCap, Clock, MessageSquare } from "lucide-react";
import { useAuth, DASHBOARD_HOME } from "../../context/AuthContext";
import BrandLogo from "../../components/BrandLogo";
import api, { fileUrl, fmtINR } from "../../lib/api";
import { Dialog, DialogContent } from "../../components/ui/dialog";

export const MKT_CSS = `
@keyframes mkt-grad { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
@keyframes mkt-shine { 0% { transform: translateX(-120%) skewX(-18deg); } 100% { transform: translateX(240%) skewX(-18deg); } }
.mkt-hero { background: linear-gradient(115deg, #007A4D 0%, #065f46 20%, #1d4ed8 55%, #06B6D4 80%, #7C3AED 100%); background-size: 220% 220%; animation: mkt-grad 18s ease infinite; }
.mkt-glass { background: rgba(255,255,255,.10); backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px); border: 1px solid rgba(255,255,255,.22); }
.mkt-card { border-radius: 20px; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); box-shadow: 0 8px 26px -14px rgba(15,23,42,.15); transition: transform .25s ease, box-shadow .25s ease; overflow: hidden; }
.mkt-card:hover { transform: translateY(-4px); box-shadow: 0 20px 44px -16px rgba(15,23,42,.25); }
.mkt-grad-fallback { background: linear-gradient(135deg, #007A4D, #2563EB 55%, #7C3AED); }
.mkt-featured { background: linear-gradient(100deg, #FFD700, #F5B700); color: #3f2d00; font-size: 10px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; padding: 4px 10px; border-radius: 999px; box-shadow: 0 4px 14px -4px rgba(245,183,0,.6); }
.mkt-cat-chip { background: rgba(15,23,42,.72); color: #fff; font-size: 10px; font-weight: 700; padding: 4px 10px; border-radius: 999px; backdrop-filter: blur(6px); }
.mkt-shine { position: relative; overflow: hidden; }
.mkt-shine::after { content: ""; position: absolute; top: 0; bottom: 0; width: 40%; background: linear-gradient(90deg, transparent, rgba(255,255,255,.35), transparent); animation: mkt-shine 5s ease-in-out infinite; pointer-events: none; }
.mkt-sec-title { background: linear-gradient(100deg, #007A4D, #2563EB 60%, #7C3AED); -webkit-background-clip: text; background-clip: text; color: transparent; }
@media (prefers-reduced-motion: reduce) { .mkt-hero, .mkt-shine::after { animation: none !important; } }
`;

export const KIND_ICON = { service: Briefcase, product: ShoppingBag, course: GraduationCap };
export const KIND_URL = { service: "services", product: "products", course: "courses" };
export const URL_KIND = { services: "service", products: "product", courses: "course" };
export const KIND_LABEL = { service: "Service", product: "Product", course: "Course" };

export function MktHeader() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [fl, setFl] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (user) api.get("/mkt/freelancer/me").then((r) => setFl(r.data)).catch(() => {});
  }, [user]);
  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3 sm:gap-4">
        <Link to="/" data-testid="mkt-logo" className="shrink-0"><BrandLogo /></Link>
        <Link to="/marketplace" data-testid="mkt-nav-marketplace" className="hidden lg:block text-sm font-bold text-gradient shrink-0">Marketplace</Link>
        <Link to={fl?.active ? "/freelancer" : "/join-freelancer"} data-testid="mkt-nav-become-freelancer"
          className="hidden lg:block text-sm font-bold text-muted-foreground hover:text-foreground transition-colors shrink-0">
          {fl?.active ? "Freelancer Dashboard" : "Become a Freelancer"}
        </Link>
        <Link to="/videos" data-testid="mkt-nav-videos" className="hidden lg:block text-sm font-bold text-muted-foreground hover:text-foreground transition-colors shrink-0">Videos</Link>
        <form className="flex-1 max-w-xl" onSubmit={(e) => { e.preventDefault(); navigate(`/marketplace/search?q=${encodeURIComponent(q)}`); }}>
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input data-testid="mkt-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search services, products, courses, freelancers…"
              className="w-full h-10 rounded-full border border-border bg-muted/50 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
          </div>
        </form>
        <div className="flex items-center gap-1.5 ml-auto shrink-0">
          {user && <Link to="/mkt/messages" data-testid="mkt-nav-messages" className="hidden md:inline-flex text-xs font-bold px-3 py-2 rounded-full hover:bg-muted transition-colors">Messages</Link>}
          {user && <Link to="/mkt/orders" data-testid="mkt-nav-orders" className="hidden md:inline-flex text-xs font-bold px-3 py-2 rounded-full hover:bg-muted transition-colors">Orders</Link>}
          {user && <Link to="/mkt/account" data-testid="mkt-nav-account" className="hidden md:inline-flex text-xs font-bold px-3 py-2 rounded-full hover:bg-muted transition-colors">Account</Link>}
          {!user && (
            <>
              <Link to="/mkt/auth?mode=register" data-testid="mkt-nav-register"
                className="rounded-full border-2 border-primary text-primary text-xs font-bold px-4 py-2 hover:bg-primary/5 transition-colors">Register Free</Link>
              <Link to="/mkt/auth" data-testid="mkt-nav-login"
                className="rounded-full gradient-bg text-white text-xs font-bold px-4 py-2 glow-sm">Sign In</Link>
            </>
          )}
          {user && (
            <Link to={DASHBOARD_HOME[user.role] || "/mkt/account"} data-testid="mkt-nav-dashboard"
              className="rounded-full gradient-bg text-white text-xs font-bold px-4 py-2 glow-sm">
              {user.role === "PARTNER" ? "Partner Dashboard" : "Dashboard"}
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

export const Stars = ({ rating = 0, className = "h-3.5 w-3.5" }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${className} ${i <= Math.round(rating) ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"}`} />
    ))}
  </span>
);

export function AuthGateDialog({ open, onOpenChange, returnUrl = "/marketplace", action = "chat with this freelancer" }) {
  const ret = encodeURIComponent(returnUrl);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="auth-gate-dialog" className="max-w-md text-center">
        <div className="mx-auto h-14 w-14 rounded-2xl mkt-grad-fallback flex items-center justify-center mb-2">
          <MessageSquare className="h-7 w-7 text-white" />
        </div>
        <h3 className="font-display font-extrabold text-xl">Create your free MILTROO account</h3>
        <p className="text-sm text-muted-foreground">to {action}. Registration is 100% free — pay only for what you buy.</p>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <Link to={`/mkt/auth?mode=register&return=${ret}`} data-testid="auth-gate-register"
            className="h-11 leading-[2.75rem] rounded-full font-bold text-sm text-white gradient-bg glow-sm">Register Free</Link>
          <Link to={`/mkt/auth?return=${ret}`} data-testid="auth-gate-login"
            className="h-11 leading-[2.75rem] rounded-full font-bold text-sm border-2 border-border hover:bg-muted transition-colors">Login</Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function useChatGate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [gate, setGate] = useState(null);
  const openChat = (uid, action) => {
    if (!uid) return;
    if (user) navigate(`/mkt/messages/${uid}`);
    else setGate({ returnUrl: `/mkt/messages/${uid}`, action });
  };
  const gateEl = <AuthGateDialog open={!!gate} onOpenChange={() => setGate(null)} returnUrl={gate?.returnUrl || "/marketplace"} action={gate?.action || "chat with this freelancer"} />;
  return [openChat, gateEl];
}

const Avatar = ({ name, photo }) => photo
  ? <img src={fileUrl(photo)} alt={name} className="h-7 w-7 rounded-full object-cover shrink-0" />
  : <div className="h-7 w-7 rounded-full mkt-grad-fallback flex items-center justify-center font-display font-extrabold text-white text-[10px] shrink-0">
      {(name || "F").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
    </div>;

export function ItemCard({ item, kind, delay = 0 }) {
  const navigate = useNavigate();
  const [openChat, gateEl] = useChatGate();
  const Icon = KIND_ICON[kind] || Briefcase;
  const url = `/marketplace/${KIND_URL[kind]}/${item.id}`;
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(delay * 0.05, 0.4) }} className="h-full">
      {gateEl}
      <div data-testid={`mkt-${kind}-card-${item.id}`} className="mkt-card group h-full flex flex-col">
        <div className="relative h-36 overflow-hidden cursor-pointer shrink-0" onClick={() => navigate(url)}>
          {item.image
            ? <img src={fileUrl(item.image)} alt={item.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
            : <div className="h-full w-full mkt-grad-fallback flex items-center justify-center"><Icon className="h-9 w-9 text-white/80" /></div>}
          {item.featured && <span className="absolute top-3 left-3 mkt-featured">Featured</span>}
          {item.category && <span className="absolute bottom-3 left-3 mkt-cat-chip">{item.category}</span>}
        </div>
        <div className="p-4 flex flex-col flex-1 gap-2">
          <button data-testid={`mkt-${kind}-seller-${item.id}`} onClick={() => item.freelancer_slug && navigate(`/marketplace/f/${item.freelancer_slug}`)}
            className="flex items-center gap-2 text-left">
            <Avatar name={item.freelancer_name} />
            <span className="text-xs font-bold truncate">{item.freelancer_name}</span>
            <span className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />{item.rating || 0} ({item.reviews || 0})
            </span>
          </button>
          <p className="font-display font-bold text-[15px] leading-snug line-clamp-2 min-h-[2.5rem] cursor-pointer" onClick={() => navigate(url)}>{item.title}</p>
          <div className="flex items-center justify-between text-xs text-muted-foreground mt-auto">
            <span className="flex items-center gap-1">
              {kind === "service" && item.delivery_days ? <><Clock className="h-3 w-3" />{item.delivery_days}d delivery · </> : null}
              {item.sales || 0} sold
            </span>
            <span className="font-display font-extrabold text-base mkt-sec-title">{fmtINR(item.price)}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button data-testid={`mkt-${kind}-view-${item.id}`} onClick={() => navigate(url)}
              className="h-9 rounded-full text-xs font-bold gradient-bg text-white active:scale-95 transition-transform">View {KIND_LABEL[kind]}</button>
            <button data-testid={`mkt-${kind}-chat-${item.id}`} onClick={() => openChat(item.seller_id, `chat about “${item.title}”`)}
              className="h-9 rounded-full text-xs font-bold border-2 border-border hover:bg-muted transition-colors flex items-center justify-center gap-1">
              <MessageSquare className="h-3.5 w-3.5" />Chat
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function FreelancerCard({ f, delay = 0 }) {
  const navigate = useNavigate();
  const [openChat, gateEl] = useChatGate();
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(delay * 0.05, 0.4) }} className="h-full">
      {gateEl}
      <div data-testid={`mkt-freelancer-card-${f.slug}`} className="mkt-card mkt-shine p-5 text-center space-y-3 h-full flex flex-col">
        <div className="flex justify-center cursor-pointer" onClick={() => navigate(`/marketplace/f/${f.slug}`)}>
          {f.photo
            ? <img src={fileUrl(f.photo)} alt={f.display_name} className="h-16 w-16 rounded-full object-cover ring-4 ring-primary/15" />
            : <div className="h-16 w-16 rounded-full mkt-grad-fallback flex items-center justify-center font-display font-extrabold text-white text-xl ring-4 ring-primary/15">
                {(f.display_name || "F").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
              </div>}
        </div>
        <div className="cursor-pointer" onClick={() => navigate(`/marketplace/f/${f.slug}`)}>
          <p className="font-display font-bold flex items-center justify-center gap-1.5">
            {f.display_name}
            {f.verified && <BadgeCheck className="h-4 w-4 text-sky-500" />}
          </p>
          {f.title && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{f.title}</p>}
          {f.location && <p className="text-[11px] text-muted-foreground mt-1 flex items-center justify-center gap-1"><MapPin className="h-3 w-3" />{f.location}</p>}
        </div>
        <div className="flex flex-wrap justify-center gap-1.5">
          {(f.skills || []).slice(0, 3).map((s) => (
            <span key={s} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{s}</span>
          ))}
        </div>
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Stars rating={f.rating} className="h-3 w-3" />
          <span>({f.reviews || 0}) · {f.completed_orders || 0} orders</span>
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1 mt-auto">
          <button data-testid={`mkt-freelancer-view-${f.slug}`} onClick={() => navigate(`/marketplace/f/${f.slug}`)}
            className="h-9 rounded-full text-xs font-bold gradient-bg text-white active:scale-95 transition-transform">View Profile</button>
          <button data-testid={`mkt-freelancer-chat-${f.slug}`} onClick={() => openChat(f.user_id, `chat with ${f.display_name}`)}
            className="h-9 rounded-full text-xs font-bold border-2 border-border hover:bg-muted transition-colors">Chat</button>
        </div>
      </div>
    </motion.div>
  );
}

export const SectionHead = ({ title, sub, action }) => (
  <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
    <div>
      <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight mkt-sec-title">{title}</h2>
      {sub && <p className="text-sm text-muted-foreground mt-1">{sub}</p>}
    </div>
    {action}
  </div>
);
